import { useState, useCallback } from 'react';
import { useFrameCategories } from '@/hooks/useFrameCategories';
import { api } from '@/lib/api';
import type {
  CreateFrameCategoryRequest,
  UpdateFrameCategoryRequest,
  FrameCategory,
} from '@photobooth/types';

export function FrameCategoriesPage() {
  const { frameCategories, isLoading, error, refetch } = useFrameCategories();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', isActive: true });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    slug: '',
    isActive: true,
  });
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [draggedId, setDraggedId] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const data: CreateFrameCategoryRequest = {
        name: formData.name,
        displayOrder: frameCategories.length,
        isActive: formData.isActive,
      };
      const response = await api.frameCategories.create(data);
      if (response.success) {
        setFormData({ name: '', isActive: true });
        setShowForm(false);
        refetch();
      } else {
        alert('Failed to create frame category');
      }
    } catch (err) {
      alert('Failed to create frame category');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (frameCategoryId: string) => {
    if (!confirm('Are you sure you want to delete this frame category?')) return;
    try {
      const response = await api.frameCategories.delete(frameCategoryId);
      if (response.success) {
        refetch();
      } else {
        alert(response.error?.message || 'Failed to delete. It may have frames assigned.');
      }
    } catch (err) {
      alert('Failed to delete frame category');
    }
  };

  const handleEditClick = (cat: FrameCategory) => {
    setEditingId(cat.frameCategoryId);
    setEditFormData({
      name: cat.name,
      slug: cat.slug,
      isActive: cat.isActive,
    });
  };

  const handleUpdate = async (e: React.FormEvent, frameCategoryId: string) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const data: UpdateFrameCategoryRequest = {
        name: editFormData.name,
        slug: editFormData.slug.trim() || undefined,
        displayOrder: frameCategories.find((c) => c.frameCategoryId === frameCategoryId)?.displayOrder ?? 0,
        isActive: editFormData.isActive,
      };
      const response = await api.frameCategories.update(frameCategoryId, data);
      if (response.success) {
        setEditingId(null);
        refetch();
      } else {
        alert('Failed to update frame category');
      }
    } catch (err) {
      alert('Failed to update frame category');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleDragStart = useCallback((e: React.DragEvent, frameCategoryId: string) => {
    setDraggedId(frameCategoryId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', frameCategoryId);
    e.dataTransfer.setData('application/json', JSON.stringify({ frameCategoryId }));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, frameCategoryId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedId && draggedId !== frameCategoryId) {
      setDragOverId(frameCategoryId);
    }
  }, [draggedId]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent, dropTargetId: string) => {
      e.preventDefault();
      setDragOverId(null);
      setDraggedId(null);

      const dragged = e.dataTransfer.getData('text/plain');
      if (!dragged || dragged === dropTargetId) return;

      const oldIndex = frameCategories.findIndex((c) => c.frameCategoryId === dragged);
      const newIndex = frameCategories.findIndex((c) => c.frameCategoryId === dropTargetId);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = [...frameCategories];
      const [moved] = reordered.splice(oldIndex, 1);
      if (!moved) {
        return;
      }
      reordered.splice(newIndex, 0, moved);

      setIsSubmitting(true);
      try {
        const updates = reordered
          .map((cat, index) => ({ cat, newOrder: index }))
          .filter(({ cat, newOrder }) => cat.displayOrder !== newOrder);

        for (const { cat, newOrder } of updates) {
          const response = await api.frameCategories.update(cat.frameCategoryId, {
            name: cat.name,
            displayOrder: newOrder,
            isActive: cat.isActive,
          });
          if (!response.success) {
            alert('Failed to update order');
            return;
          }
        }
        refetch();
      } catch (err) {
        alert('Failed to update order');
      } finally {
        setIsSubmitting(false);
      }
    },
    [frameCategories, refetch]
  );

  const handleDragEnd = useCallback(() => {
    setDragOverId(null);
    setDraggedId(null);
  }, []);

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-text">Frame Categories</h1>
          <p className="text-text-muted mt-1">
            Organize frames into categories. Drag rows to reorder.
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-primary text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          {showForm ? 'Cancel' : '+ New Frame Category'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {showForm && (
        <div className="bg-surface rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold text-text mb-4">Create Frame Category</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text mb-1">Name *</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="e.g. Photobooth, Thematic, High Angle"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-2 focus:ring-primary"
              />
              <label htmlFor="isActive" className="text-sm font-medium text-text">
                Active
              </label>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Frame Category'}
            </button>
          </form>
        </div>
      )}

      <div className="bg-surface rounded-lg shadow overflow-hidden">
        {frameCategories.length === 0 ? (
          <div className="p-12 text-center text-text-muted">
            No frame categories yet. Create one to get started.
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase w-12">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">
                  Slug
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-text-muted uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {frameCategories.map((cat) => (
                <tr
                  key={cat.frameCategoryId}
                  draggable={editingId !== cat.frameCategoryId}
                  onDragStart={(e) => handleDragStart(e, cat.frameCategoryId)}
                  onDragOver={(e) => handleDragOver(e, cat.frameCategoryId)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, cat.frameCategoryId)}
                  onDragEnd={handleDragEnd}
                  className={`${
                    editingId !== cat.frameCategoryId ? 'cursor-grab active:cursor-grabbing' : ''
                  } ${dragOverId === cat.frameCategoryId ? 'bg-blue-50' : ''} ${
                    draggedId === cat.frameCategoryId ? 'opacity-50' : ''
                  }`}
                >
                  <td className="px-4 py-3 text-text-muted text-sm">
                    <span className="inline-block" title="Drag to reorder">
                      ⋮⋮
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingId === cat.frameCategoryId ? (
                      <form
                        onSubmit={(e) => handleUpdate(e, cat.frameCategoryId)}
                        className="flex flex-col gap-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="text"
                          required
                          value={editFormData.name}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, name: e.target.value })
                          }
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        <div>
                          <label className="block text-xs text-text-muted mb-1">Slug</label>
                          <input
                            type="text"
                            value={editFormData.slug}
                            onChange={(e) =>
                              setEditFormData({ ...editFormData, slug: e.target.value })
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="url-friendly-slug"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`edit-isActive-${cat.frameCategoryId}`}
                            checked={editFormData.isActive}
                            onChange={(e) =>
                              setEditFormData({ ...editFormData, isActive: e.target.checked })
                            }
                            className="w-4 h-4 text-primary border-gray-300 rounded"
                          />
                          <label
                            htmlFor={`edit-isActive-${cat.frameCategoryId}`}
                            className="text-sm"
                          >
                            Active
                          </label>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className="text-sm bg-primary text-white py-1 px-3 rounded hover:bg-blue-700"
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelEdit}
                            className="text-sm bg-gray-200 py-1 px-3 rounded hover:bg-gray-300"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <span className="font-medium text-text">{cat.name}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-text-muted">
                    {cat.slug}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        cat.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {cat.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {editingId !== cat.frameCategoryId && (
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => handleEditClick(cat)}
                          className="text-primary hover:text-blue-700 text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(cat.frameCategoryId)}
                          className="text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
}
